import React, {useContext, useEffect} from 'react';
import {Button, Grid, Typography} from '@material-ui/core';
import {Text} from 'components/forms/fields';
import {FormProvider, useForm} from 'react-hook-form';
import Joi from 'joi';
import {joiResolver} from '@hookform/resolvers/joi';
import joiMessages from 'components/forms/locale/validation';
import WidgetCard from '../WidgetCard';
import {CircularProgress} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import {useGetMMLDActive} from 'hooks';
import {PersonSearch, PersonSearchLight} from '../icons/icons';
import InfoModal from 'components/widgets/tooltips/InfoModal';
import content from 'components/widgets/tooltips/InfoModalContent';
import {P} from 'components/TypographyShortcuts';
import {UserContext} from 'context/UserContext';
import Show from 'components/utils/Show';
import definedSchemas from 'components/forms/fields/definedSchemas';
import {useTheme, makeStyles} from '@material-ui/core/styles';

const schema = {
    lastname: Joi.string()
        .required()
        .messages({'any.required': 'Please enter your last name.'}),
    refnum: definedSchemas.mmcNumberRequired.validation.messages({
        'number.min': 'MMC Reference Number must be 6 or 7 digits long',
        'number.max': 'MMC Reference Number must be 6 or 7 digits long',
        'any.required': 'Please enter your reference number.',
    }),
};

const AddMMCCore = ({intro, ButtonProps}) => {
    const {refetchUser, user} = useContext(UserContext);
    const methods = useForm({
        defaultValues: {
            lastname: user?.lastname || '',
            refnum: '',
        },
        mode: 'onTouched',
        resolver: joiResolver(
            Joi.object(schema).messages(joiMessages).unknown(true)
        ),
    });

    const {get, data: mmld, isLoading: loading, error} = useGetMMLDActive();
    const onSubmit = data => {
        get({
            lastname: data.lastname.replaceAll("'", "''"),
            refnum: data.refnum,
            only_recent: true,
            fetch: false,
        });
    };

    useEffect(() => {
        if (mmld && mmld?.status !== 'error') {
            // Update UserContext's MMLD
            refetchUser();
        }
        //eslint-disable-next-line
    }, [mmld]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} id='add-mmc'>
                <Grid container spacing={2} style={{padding: '1em'}}>
                    <Show condition={intro}>{intro}</Show>
                    <Grid item container xs={12}>
                        <Grid item xs={11}>
                            <Text name='lastname' label='Last Name' />
                        </Grid>
                    </Grid>
                    <Grid item xs={11}>
                        <Text name='refnum' label='Reference Number' />
                    </Grid>
                    <Grid item xs={1}>
                        <InfoModal content={content.mmcrefnum} />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            type='submit'
                            variant='contained'
                            color='secondary'
                            startIcon={<PersonSearchLight />}
                            {...ButtonProps}
                            form='add-mmc'
                        >
                            Easy Lookup
                        </Button>
                    </Grid>
                    {loading ? (
                        <Grid item xs={12} justifyContent='center'>
                            <Alert severity='info'>
                                <P>MMC lookup takes about 10 seconds. Hold tight!</P>
                                <CircularProgress />
                            </Alert>
                        </Grid>
                    ) : (
                        <Grid item xs={12} />
                    )}
                    {mmld?.status === 'error' ? (
                        <Grid item xs={12} justifyContent='center'>
                            <Alert severity='warning'>
                                No MMC found for that last name and reference number.
                                Please double check the info and try the search
                                again.
                            </Alert>
                        </Grid>
                    ) : error ? (
                        <Grid item xs={12} justifyContent='center'>
                            <Alert severity='warning'>
                                Something went wrong with our system. Please try
                                again later.
                            </Alert>
                        </Grid>
                    ) : (
                        <Grid item xs={12} />
                    )}
                </Grid>
            </form>
        </FormProvider>
    );
};

const useStyles = makeStyles(theme => ({
    personSearchIcon: {
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '3em',
        height: '3em',
    },
}));

const AddMMC = () => {
    const classes = useStyles();
    const theme = useTheme();
    return (
        <WidgetCard title='Enter Your MMC'>
            <AddMMCCore
                intro={[
                    <Grid item xs={12} key='preamble'>
                        <Typography variant='h6' align='center'>
                            To assess your qualifications and keep track of
                            expiration dates, we need to know what credentials you
                            already have. Use our Easy Lookup tool to add your MMC!
                        </Typography>
                    </Grid>,
                    <Grid item xs={12} key='icon'>
                        {theme.palette.type === 'dark' ? (
                            <PersonSearchLight
                                className={classes.personSearchIcon}
                            />
                        ) : (
                            <PersonSearch className={classes.personSearchIcon} />
                        )}
                    </Grid>,
                    <Grid item xs={12} key='more-preamble'>
                        <Typography variant='h6' align='center'>
                            To use the Easy Lookup tool, enter your last name (as it
                            appears on your MMC) and reference number below.
                        </Typography>
                    </Grid>,
                ]}
            />
        </WidgetCard>
    );
};

export default AddMMC;
export {AddMMCCore};
